'use client';

import { useState, useEffect } from 'react';
import { Target, Clock, CheckCircle } from 'lucide-react';

interface Challenge {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  theme: string;
  durationDays: number;
  rewardType: string;
  rewardValue: string;
  isActive: boolean;
  userStatus: string | null;
  progress: number;
  target: number;
  progressPercent: number;
  startedAt: string | null;
  expiresAt: string | null;
}

export default function ChallengeCards() {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/secure/challenges', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        setChallenges(data.challenges || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const startChallenge = async (challengeId: string) => {
    setStarting(challengeId);
    try {
      const res = await fetch('/api/secure/challenges', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId }),
      });
      if (res.ok) {
        setChallenges(prev =>
          prev.map(c =>
            c.id === challengeId ? { ...c, isActive: true, userStatus: 'active', progress: 0 } : c
          )
        );
      }
    } catch {
      // Ignore
    } finally {
      setStarting(null);
    }
  };

  const daysRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) {
    return (
      <div className="text-foreground-muted text-sm py-4">Loading challenges...</div>
    );
  }

  if (challenges.length === 0) return null;

  // Split into active and available
  const active = challenges.filter(c => c.isActive);
  const available = challenges.filter(c => !c.isActive);

  return (
    <div className="space-y-4">
      {/* Active challenges */}
      {active.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground-tertiary uppercase tracking-wider">Active Challenges</h3>
          {active.map(c => {
            const days = daysRemaining(c.expiresAt);
            return (
              <div key={c.id} className="p-4 rounded-lg border border-border bg-accent-muted">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{c.icon}</span>
                    <div>
                      <div className="font-semibold text-foreground text-sm">{c.name}</div>
                      <div className="text-xs text-foreground-tertiary">{c.description}</div>
                    </div>
                  </div>
                  {days !== null && (
                    <div className="flex items-center gap-1 text-xs text-foreground-muted whitespace-nowrap">
                      <Clock className="w-3 h-3" />
                      {days}d left
                    </div>
                  )}
                </div>
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-foreground-tertiary mb-1">
                    <span>{c.progress} / {c.target}</span>
                    <span>{c.progressPercent}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent rounded-full transition-all duration-500"
                      style={{ width: `${c.progressPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Available challenges */}
      {available.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground-tertiary uppercase tracking-wider">Available Challenges</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {available.slice(0, 4).map(c => (
              <button
                key={c.id}
                onClick={() => startChallenge(c.id)}
                disabled={starting === c.id}
                className="p-3 rounded-lg border border-border-muted hover:border-border text-left transition-all group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span>{c.icon}</span>
                  <span className="font-semibold text-foreground text-sm group-hover:text-accent transition-colors">{c.name}</span>
                </div>
                <div className="text-xs text-foreground-muted line-clamp-2">{c.description}</div>
                <div className="flex items-center gap-2 mt-2 text-xs text-foreground-muted">
                  <Target className="w-3 h-3" />
                  <span>{c.durationDays} days</span>
                  {c.rewardType === 'badge' && <span>&middot; Earns badge</span>}
                  {c.rewardType === 'streak_bonus' && <span>&middot; +{c.rewardValue} streak days</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
